import { USE_MOCKS } from '@/config';
import { delay, request } from './client';

/**
 * The signed-in user's cart, stored on their account.
 *
 * None of these take a username: the backend reads it from the session token,
 * so there is no way to fetch or overwrite anyone else's cart.
 *
 * A cart holds only book ids, quantities and chosen adaptations. Prices and
 * titles are joined from the catalogue when it is read, so a cart left for a
 * week can never quote a price the shop has since changed.
 */

/** Mock-mode storage. The mock IS the server here, so it has to remember. */
const MOCK_KEY = 'tub.mockCarts';

function readMockCarts() {
  try {
    return JSON.parse(window.localStorage.getItem(MOCK_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeMockCart(username, lines) {
  try {
    const carts = readMockCarts();
    carts[username] = lines;
    window.localStorage.setItem(MOCK_KEY, JSON.stringify(carts));
  } catch {
    /* storage unavailable — the in-memory cart still covers this visit */
  }
  return { lines };
}

/** Strips the joined-at-read fields so only cart facts travel. */
const toWire = (lines) =>
  lines.map((line) => ({
    id: String(line.id),
    qty: line.qty,
    languages: line.languages ?? [],
  }));

export async function getCart(username, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(120);
    return { lines: readMockCarts()[username] ?? [] };
  }
  return request('/api/cart', { signal });
}

export async function saveCart(username, lines, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(80);
    return writeMockCart(username, toWire(lines));
  }
  return request('/api/cart', { method: 'PUT', body: { lines: toWire(lines) }, signal });
}

/**
 * Folds a browser cart into the stored one. This is what sign-in does: books
 * collected before signing in join what the account already had, rather than
 * replacing it or being thrown away.
 */
export async function mergeCart(username, lines, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(120);
    const stored = readMockCarts()[username] ?? [];
    const byId = new Map();
    for (const line of [...stored, ...toWire(lines)]) {
      const existing = byId.get(line.id);
      byId.set(
        line.id,
        existing
          ? {
              ...existing,
              qty: Math.max(existing.qty, line.qty),
              languages: [...new Set([...existing.languages, ...line.languages])].sort(),
            }
          : { ...line, languages: [...new Set(line.languages)].sort() },
      );
    }
    return writeMockCart(username, [...byId.values()]);
  }
  return request('/api/cart/merge', { method: 'POST', body: { lines: toWire(lines) }, signal });
}

/**
 * Mock-mode counterpart to cart_service.remove_books: takes the books just
 * bought out of the stored cart, so the mock behaves like the server it stands
 * in for and the purchase flow can be tested without one.
 */
export function forgetMockCartBooks(username, items) {
  if (!USE_MOCKS || !username) return;
  const drop = new Set(items.map((item) => String(item.id)));
  const stored = readMockCarts()[username] ?? [];
  writeMockCart(
    username,
    stored.filter((line) => !drop.has(String(line.id))),
  );
}

